import React, { useEffect, useState } from 'react';
import { styled } from 'styled-components';
import { Box, Button, ButtonGroup, Grid, Typography } from '@mui/material';
import { DemoContainer } from '@mui/x-date-pickers/internals/demo';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import dayjs, { Dayjs } from 'dayjs';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import MigraineCalendar from '../MigraineCalendar/MigraineCalendar';

import { GENERAL_FONT, MHColours } from '../../constants/theme';
import PatientProfile from '../PatientProfile/PatientProfile';
import TimePeriodOverview from '../TimePeriodOverview/TimePeriodOverview';
import Charts from '../Charts/Charts';
import { RootState } from '../../store/types/sharedTypes';

import type { AppDispatch } from '../../store';

import { getFormattedPersonalNumber } from '../../lib/helpers/personalNumber';
import {
  getDiaryEntries,
  getDiaryEntriesStatistics,
  getStatisticsFile,
} from '../../store/actions/general';
import {
  IDateRange,
  IDateRangeStatistics,
  IDiaryEntry,
} from '../../lib/interfaces';
import { getMonthRange } from '../../lib/helpers/date';
import Loader from '../Loader/Loader';
import {
  getStatistics,
  getStatisticsByMonthsOrWeeks,
  sortDiaryEntriesByDate,
} from '../../lib/helpers/diary';
import MessageBoard from '../MessageBoard/MessageBoard';
import TreatmentEffectDashboard from '../TreatmentEffectDashboard/TreatmentEffectDashboard';
import FormOverview from '../PatientForms/FormOverview';
import VideoCallContainer from '../VideoVisit/VideoCallContainer';

const Container = styled.div`
  margin: auto;
`;
const ContentContainer = styled.div`
  margin: 20px 0px 30px 0px;
`;

export const profileBoxStyle = {
  boxShadow: 3,
  width: '100%',
  p: 3,
  m: 1,
  borderRadius: 0,
  textAlign: 'left',
  fontSize: '0.875rem',
  fontWeight: '700',
};

const datePickerSx = {
  maxWidth: '200px',
  minWidth: '10px',
  '& .MuiFormLabel-root': {
    color: MHColours.mhGreen,
  },
  '& .MuiOutlinedInput-notchedOutline': {
    border: `3px solid${MHColours.mhGreen}`,
  },
};

export const exportButtonStyle = {
  padding: '6px 16px 6px 16px',
  borderRadius: '4px',
  marginTop: '16px',
  marginBottom: '24px',
  marginLeft: '16px',
  backgroundColor: MHColours.mhGreen,
  fontFamily: GENERAL_FONT,
  color: 'white',
};
export const exportButtonStyleDisabled = {
  padding: '6px 16px 6px 16px',
  borderRadius: '4px',
  marginTop: '16px',
  marginBottom: '24px',
  marginLeft: '16px',
  backgroundColor: MHColours.mhGreen,
  fontFamily: GENERAL_FONT,
  color: 'white',
  opacity: '50%',
};

type SelectedView =
  | 'profile'
  | 'diary'
  | 'messages'
  | 'forms'
  | 'treatmentEffect';

const PatientDetails = () => {
  const [selectedView, setSelectedView] = useState<SelectedView>('diary');
  const [minDateFrom] = useState<Dayjs>(dayjs().subtract(360, 'day'));

  const getInitialDateFrom = (): Dayjs => {
    const today = dayjs();
    const startOfCurrentMonth = today.startOf('month');
    return startOfCurrentMonth.subtract(3, 'month');
  };

  const [dateFrom, setDateFrom] = useState<Dayjs>(getInitialDateFrom());

  const [dateTo, setDateTo] = useState<Dayjs>(dayjs());
  const [statistics, setStatistics] = useState<IDateRangeStatistics>();

  const [statisticsByMonths, setStatisticsByMonths] = useState<
    Record<string, IDateRangeStatistics> | undefined
  >();

  const [sortedStatisticsEntries, setSortedStatisticsEntries] = useState<
    IDiaryEntry[]
  >([]);
  const [datePickerError, setDatePickerError] = useState(false);

  const [jumpToDate, setJumpToDate] = useState<Dayjs>(dayjs());
  const { patientDetails, calendarStatistics } = useSelector(
    (state: RootState) => state.general
  );

  const dispatch = useDispatch<AppDispatch>();

  const { t } = useTranslation();
  const params = useParams();

  const currentMonth: IDateRange = getMonthRange();

  useEffect(() => {
    if (params.id) {
      dispatch(
        getDiaryEntries(currentMonth.startDate, currentMonth.endDate, params.id)
      );
      dispatch(
        getDiaryEntriesStatistics(
          dateFrom.toString(),
          dateTo.toString(),
          params.id
        )
      );
    }

    if (!patientDetails?.isPatient) {
      setSelectedView('profile');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- loads once per mount; Dashboard swaps this view for a Loader while patient details load, so switching patient remounts it
  }, []);

  useEffect(() => {
    if (calendarStatistics?.statisticsEntries) {
      const sorted = sortDiaryEntriesByDate(
        calendarStatistics.statisticsEntries
      );
      setSortedStatisticsEntries(sorted);
      setStatistics(getStatistics(sorted));
      const result = getStatisticsByMonthsOrWeeks(sorted);
      setStatisticsByMonths(result);
    }
  }, [calendarStatistics.statisticsEntries]);

  const groupButtonStyleSelected = {
    backgroundColor: MHColours.mhGreen,
    color: 'white',
    fontFamily: GENERAL_FONT,
  };
  const groupButtonStyleUnelected = {
    backgroundColor: MHColours.mhBackground,
    color: MHColours.mhRed,
    fontFamily: GENERAL_FONT,
  };

  const checkDateValidity = (date: Dayjs | null) => {
    if (!date || !date.isValid()) {
      setDatePickerError(true);
      return false;
    }
    if (date.year() < 2000 || date.year() > 2050) {
      return false;
    }
    return true;
  };

  const onDateFromChanged = (event: Dayjs | null) => {
    if (!checkDateValidity(event)) {
      setDatePickerError(true);
      return;
    }
    if (!event || !patientDetails?._id) return;

    if (event.year() < 2000 || event.year() > 2050) {
      return;
    }
    setDatePickerError(false);
    const isBefore = event.isBefore(dateTo);
    if (!isBefore) {
      setDateTo(event);
    }
    dispatch(
      getDiaryEntriesStatistics(
        event.toString(),
        event.isBefore(dateTo) ? dateTo.toString() : event.toString(),
        patientDetails._id
      )
    );
    setDateFrom(event);
  };

  const onJumpToDateChenged = (event: Dayjs | null) => {
    if (!checkDateValidity(event)) {
      setDatePickerError(true);
      return;
    }
    if (!event || !params.id) return;

    const jumpToMonth: IDateRange = getMonthRange(event.toString());
    dispatch(
      getDiaryEntries(jumpToMonth.startDate, jumpToMonth.endDate, params.id)
    );
    setJumpToDate(event);
  };

  const onDateToChanged = (event: Dayjs | null) => {
    if (!checkDateValidity(event)) {
      setDatePickerError(true);
      return;
    }
    if (!event || !patientDetails?._id) return;

    setDatePickerError(false);
    dispatch(
      getDiaryEntriesStatistics(
        dateFrom.toString(),
        event.toString(),
        patientDetails._id
      )
    );
    setDateTo(event);
  };

  const exportPDF = () => {
    if (patientDetails?._id) {
      dispatch(
        getStatisticsFile(
          dateFrom.toString(),
          dateTo.toString(),
          patientDetails._id
        )
      );
    }
  };

  if (!patientDetails) {
    return <p> {t('patientNotFound')} </p>;
  }

  const renderSelectedView = () => {
    if (selectedView === 'messages') {
      return <MessageBoard />;
    }
    if (selectedView === 'profile') {
      return <PatientProfile patient={patientDetails} />;
    }
    if (selectedView === 'treatmentEffect') {
      return <TreatmentEffectDashboard patient={patientDetails} />;
    }
    if (selectedView === 'forms') {
      return <FormOverview patient={patientDetails} />;
    }
    return <MigraineCalendar jumpToDate={jumpToDate} />;
  };

  const renderDiaryStatistics = () => {
    if (selectedView !== 'diary') {
      return null;
    }
    if (calendarStatistics.statisticsLoader) {
      return <Loader />;
    }
    return (
      <Grid container rowSpacing={1} columnSpacing={{ xs: 1, sm: 2, md: 3 }}>
        <Grid item xs={6}>
          <Box sx={profileBoxStyle}>
            <Typography
              style={{
                fontSize: 18,
                fontWeight: 'bold',
                marginTop: '12px',
              }}
            >
              {t('periodOverview')}
            </Typography>
            <div
              style={{
                display: 'flex',
                flexDirection: 'row',
                alignContent: 'center',
              }}
            >
              <div>
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <DemoContainer components={['DatePicker']}>
                    <DatePicker
                      sx={datePickerSx}
                      value={dateFrom}
                      format="DD/MM/YYYY"
                      onChange={onDateFromChanged}
                      label={t('from')}
                      minDate={minDateFrom}
                      maxDate={dayjs()}
                    />
                    <DatePicker
                      sx={datePickerSx}
                      value={dateTo}
                      format="DD/MM/YYYY"
                      onChange={onDateToChanged}
                      label={t('to')}
                      minDate={dateFrom}
                      maxDate={dayjs()}
                    />
                  </DemoContainer>
                </LocalizationProvider>
              </div>
              <Button
                sx={{
                  textTransform: 'none',
                  height: '40px',
                  minWidth: 'fit-content',
                }}
                style={
                  Object.keys(statisticsByMonths ?? {}).length === 0 ||
                  datePickerError
                    ? exportButtonStyleDisabled
                    : exportButtonStyle
                }
                disabled={
                  Object.keys(statisticsByMonths ?? {}).length === 0 ||
                  calendarStatistics?.exportButtonLoader ||
                  datePickerError
                }
                onClick={() => exportPDF()}
              >
                {t('export')}
              </Button>
            </div>
            <TimePeriodOverview
              statistics={statistics}
              statisticsByMonths={statisticsByMonths}
              overviewButtonDisabled={datePickerError}
            />
          </Box>
        </Grid>
        <Grid item xs={6}>
          <Box sx={profileBoxStyle}>
            <Typography
              style={{
                fontSize: 18,
                fontWeight: 'bold',
                marginTop: '12px',
              }}
            >
              {t('diagram')}
            </Typography>
            <Charts
              diaryEntries={sortedStatisticsEntries}
              dateFrom={dateFrom.toString()}
              dateTo={dateTo.toString()}
            />
          </Box>
        </Grid>
      </Grid>
    );
  };

  const TabButton = styled(Button)(() => ({
    borderRadius: '5px !important',
    marginRight: '12px !important',
    textTransform: 'none',
    fontWeight: 700,
    border: 'none !important',
    minWidth: '90px !important',
    minHeight: '35px !important',
    paddingLeft: '8px !important',
    paddingRight: '8px !important',
  }));

  return (
    <Container>
      <Box sx={profileBoxStyle}>
        <VideoCallContainer patient={patientDetails} />
        <Typography
          style={{
            fontSize: 20,
            fontWeight: 'bold',
            fontFamily: GENERAL_FONT,
          }}
        >
          {`${patientDetails?.firstName} ${patientDetails?.lastName}`}
        </Typography>
        <Typography
          style={{
            fontSize: '18px',
            marginBottom: '30px',
            fontFamily: GENERAL_FONT,
            opacity: '0.5',
          }}
        >
          {getFormattedPersonalNumber(patientDetails?.personalNumber ?? '')}
        </Typography>
        {patientDetails?.isPatient === true && (
          <>
            <ButtonGroup
              disableElevation
              variant="contained"
              aria-label="Disabled elevation buttons"
            >
              <TabButton
                sx={{ textTransform: 'none' }}
                style={
                  selectedView === 'profile'
                    ? groupButtonStyleSelected
                    : groupButtonStyleUnelected
                }
                onClick={() => setSelectedView('profile')}
              >
                {t('profile')}
              </TabButton>
              <TabButton
                sx={{ textTransform: 'none' }}
                style={
                  selectedView === 'diary'
                    ? groupButtonStyleSelected
                    : groupButtonStyleUnelected
                }
                onClick={() => setSelectedView('diary')}
              >
                {t('diary')}
              </TabButton>
              <TabButton
                sx={{ textTransform: 'none' }}
                style={
                  selectedView === 'messages'
                    ? groupButtonStyleSelected
                    : groupButtonStyleUnelected
                }
                onClick={() => setSelectedView('messages')}
              >
                {t('messages')}
              </TabButton>

              <TabButton
                sx={{ textTransform: 'none' }}
                style={
                  selectedView === 'forms'
                    ? groupButtonStyleSelected
                    : groupButtonStyleUnelected
                }
                onClick={() => setSelectedView('forms')}
              >
                {t('form')}
              </TabButton>
              <TabButton
                sx={{ textTransform: 'none' }}
                style={
                  selectedView === 'treatmentEffect'
                    ? groupButtonStyleSelected
                    : groupButtonStyleUnelected
                }
                onClick={() => setSelectedView('treatmentEffect')}
              >
                {t('treatmentEffectTab')}
              </TabButton>
            </ButtonGroup>
            {!selectedView && (
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DatePicker
                  sx={{ ...datePickerSx, float: 'right' }}
                  value={jumpToDate}
                  format="DD/MM/YYYY"
                  onChange={onJumpToDateChenged}
                  label={t('jumpToDate')}
                  minDate={minDateFrom}
                  maxDate={dayjs()}
                />
              </LocalizationProvider>
            )}
          </>
        )}
        <ContentContainer>{renderSelectedView()}</ContentContainer>
      </Box>
      <Container>{renderDiaryStatistics()}</Container>
    </Container>
  );
};

export default PatientDetails;
